import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const archiveProductSkill: Skill = {
  id: "archiveProduct",
  description:
    "Archiva un producto (lo oculta del catálogo sin eliminarlo). Útil cuando el usuario pida 'desactiva', 'oculta', 'archiva' o 'quita del menú' un producto. Si solo conoces el nombre, usa searchProduct primero. CONFIRMA antes con el usuario en texto.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      productId: { type: "string" },
    },
    required: ["restaurantId", "productId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) {
      return { ok: false, error: "Sesión no disponible." };
    }
    const restaurantId = String(args.restaurantId ?? "");
    const productId = String(args.productId ?? "");
    if (!restaurantId || !productId) {
      return { ok: false, error: "Faltan restaurantId o productId." };
    }

    const ok = await ctx.confirmation.confirm({
      title: "Archivar producto",
      description:
        "El producto se ocultará del catálogo y de la carta. Podrás restaurarlo más tarde si lo necesitas.",
      details: [{ label: "Producto", value: productId }],
      confirmLabel: "Archivar",
      danger: true,
    });
    if (!ok) {
      return { ok: false, error: "Acción cancelada por el usuario." };
    }

    if (ctx.follow.enabled) {
      await ctx.follow.step("Archivando producto…", { durationMs: 600 });
      await ctx.follow.clickAt(`[data-copilot-tab="productos"]`, {
        durationMs: 700,
        label: "Productos",
      });
      ctx.router.push(
        `/dashboard/restaurantes/${restaurantId}?tab=productos`,
      );
      await sleep(900);
    }

    try {
      await apiWithAuth.post(
        `/restaurants/${restaurantId}/products/${productId}/archive`,
        {},
        ctx.accessToken,
      );

      if (ctx.follow.enabled) {
        await ctx.follow.step("✓ Producto archivado", {
          durationMs: 900,
          spinner: false,
        });
        ctx.follow.clear();
      }

      emitCopilotMutation({
        scope: "products",
        action: "archived",
        restaurantId,
        id: productId,
      });

      return {
        ok: true,
        result: { productId, archived: true },
        humanSummary: "Producto archivado.",
      };
    } catch (err: any) {
      if (ctx.follow.enabled) {
        await ctx.follow.step("✗ Error al archivar", {
          durationMs: 700,
          spinner: false,
        });
        ctx.follow.clear();
      }
      return {
        ok: false,
        error:
          err?.response?.data?.message ||
          err?.message ||
          "Error archivando producto.",
      };
    }
  },
};
